import {
  getGWPTotal,
  getGwpPromotionBrand,
  getMoneyOffAmount,
  getGwpPercentage,
} from "@/utils/product/getProductFromPromoMessaging";
import { getBasketData } from "@/utils/product/getBasketData.ts";
import { getSpendStretchQualifyingBasketItemsTotal } from "@/utils/product/getPromoOffersFromBasket";
import { useEffect, useState } from "react";
import styles from "./SpendStretch.module.scss";
import { TickInCircleIcon } from "./TickInCircleIcon/TickInCircleIcon";
import Basket24PxOutlinedIcon from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/Basket24PxOutlined";
import { MessageWithCategoryAndType } from "@/utils/product/getPromoMessaging";

type SpendStretchProps = {
  promotion: MessageWithCategoryAndType;
  basketEndpoint?: string;
  targetProductId?: string;
  drawerIsOpen: boolean;
};

const SpendStretch: React.FC<SpendStretchProps> = (
  spendStretchProps: SpendStretchProps,
) => {
  const [dataFetchError, setDataFetchError] = useState<boolean>(false);
  const [GWPSpentTotal, setGWPSpentTotal] = useState<string>("");
  const [GWPTargetTotal, setGWPTargetTotal] = useState<string>("");
  const [gwpPercentage, setGwpPercentage] = useState<number>(0);
  const [remainingSpend, setRemainingSpend] = useState<string>("");
  const [dataLoading, setDataLoading] = useState<boolean>(false);

  const { promotion, basketEndpoint, targetProductId, drawerIsOpen } =
    spendStretchProps;

  useEffect(() => {
    setDataLoading(true);
    const gwpTotal = getGWPTotal(promotion.title);
    if (!gwpTotal) {
      setDataFetchError(true);
    } else {
      setGWPTargetTotal(Number(gwpTotal).toFixed(2));

      if (basketEndpoint && !dataFetchError && gwpTotal) {
        getBasketData(basketEndpoint)
          .then((basketData) => {
            if (basketData.error) {
              setDataFetchError(true);
            } else {
              const qualifyingItemsTotal =
                getSpendStretchQualifyingBasketItemsTotal(
                  promotion,
                  basketData.data,
                  targetProductId,
                );

              const percentage = getGwpPercentage(
                qualifyingItemsTotal,
                gwpTotal,
              );
              const displayedSpentTotal =
                qualifyingItemsTotal > gwpTotal
                  ? gwpTotal
                  : qualifyingItemsTotal;
              setGWPSpentTotal(Number(displayedSpentTotal).toFixed(2));
              setGwpPercentage(percentage);
              setRemainingSpend(
                Number(gwpTotal - qualifyingItemsTotal).toFixed(2),
              );
              setDataLoading(false);
            }
          })
          .catch();
      }
    }
  }, [drawerIsOpen]);

  const getGwpMessage = () => {
    let gwpMessage = null;
    const brand = getGwpPromotionBrand(promotion.title);
    const brandMessage = brand ? `on ${brand} ` : "";
    const moneyOffAmount = getMoneyOffAmount(promotion.title);
    const giftOrMoney = moneyOffAmount ? `£${moneyOffAmount} off` : "this gift";

    if (gwpPercentage === 100) {
      gwpMessage = (
        <p
          className={styles.spendStretchGwp__gwpMessage}
        >{`You have qualified for ${giftOrMoney}! Follow the steps to make your claim.`}</p>
      );
    } else {
      gwpMessage = (
        <p className={styles.spendStretchGwp__gwpMessage}>
          {`Spend `}
          <strong>{`£${remainingSpend}`}</strong>
          {` more ${brandMessage}to qualify for ${giftOrMoney}`}
        </p>
      );
    }
    return gwpMessage;
  };

  if (dataFetchError || dataLoading) {
    return null;
  }

  return (
    <div className={styles.spendStretchGwp__progressContainer}>
      {getGwpMessage()}
      <div className={styles.spendStretchGwp__progressBarAndTickContainer}>
        <div
          className={
            styles.spendStretchGwp__progressBarAndTickContainer__progressBarContainer
          }
        >
          <div
            className={
              styles.spendStretchGwp__progressBarAndTickContainer__progressBarContainer__progressBar
            }
            style={{ width: `${gwpPercentage}%` }}
            data-testid="spend-stretch-progress-bar"
          />
        </div>
        <div
          className={
            styles.spendStretchGwp__progressBarAndTickContainer__tickIcon
          }
        >
          <TickInCircleIcon
            isFilled={gwpPercentage === 100}
            visuallyHiddenClass={styles.spendStretchGwp__visuallyHidden}
          />
        </div>
      </div>
      <div className={styles.spendStretchGwp__gwpProgressMessageContainer}>
        <p
          className={
            styles.spendStretchGwp__gwpProgressMessageContainer__gwpProgressMessage
          }
        >
          <strong>{`£${GWPSpentTotal}`}</strong>
          {` of `}
          <strong>{`£${GWPTargetTotal}`}</strong>
        </p>
        <Basket24PxOutlinedIcon title="in basket" />
      </div>
    </div>
  );
};

export default SpendStretch;
